import { type FormEvent, type ReactNode, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as authApi from '../../api/auth';
import { useAuth } from '../../lib/auth';
import { ApiError } from '../../lib/http';
import { linkWhatsApp } from '../lib/basePath';
import { valorConfig } from '../lib/formato';
import { useConfiguracaoPublica } from '../lib/hooks';
import { validarNovaSenha } from '../lib/validacaoSenha';
import { CamposSenha } from '../componentes/CamposSenha';
import { TelaAuthPortal } from '../componentes/TelaAuthPortal';
import { BlocoAviso, Botao, CheckboxTermos } from '../componentes/ui';

/**
 * Primeiro acesso — definir senha (UX §5.1). Tela única: senha + confirmar +
 * checkbox de termos **obrigatório e nunca pré-marcado** (LGPD). Token na URL.
 * `POST /v1/auth/primeiro-acesso { token, senha, aceitaTermos }`.
 *
 * Contrato: a API não devolve o nome do cliente a partir do token, então a
 * saudação é genérica ("Bem-vindo!") — ver relatório (pendência de contrato).
 */
export function PortalPrimeiroAcesso(): ReactNode {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { registrarSessao } = useAuth();
  const { data: config } = useConfiguracaoPublica();

  const [senha, setSenha] = useState('');
  const [confirma, setConfirma] = useState('');
  const [aceita, setAceita] = useState(false);
  const [erroSenha, setErroSenha] = useState<string | null>(null);
  const [erroTermos, setErroTermos] = useState<string | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [linkExpirado, setLinkExpirado] = useState(!token);
  const [enviando, setEnviando] = useState(false);

  const waLoja = linkWhatsApp(config ? valorConfig(config, 'loja_whatsapp') : null);

  if (linkExpirado) {
    return (
      <TelaAuthPortal>
        <div className="flex flex-col gap-5">
          <BlocoAviso>
            Este link expirou ou já foi usado. Peça um novo pelo WhatsApp da Varga.
          </BlocoAviso>
          {waLoja && (
            <Botao variante="secundario" onClick={() => window.open(waLoja, '_blank', 'noopener')}>
              Falar com a Varga
            </Botao>
          )}
        </div>
      </TelaAuthPortal>
    );
  }

  const aoEnviar = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setErroSenha(null);
    setErroTermos(null);
    setErroGeral(null);

    let ok = true;
    const erroDeSenha = validarNovaSenha(senha, confirma);
    if (erroDeSenha) {
      setErroSenha(erroDeSenha);
      ok = false;
    }
    if (!aceita) {
      setErroTermos('É preciso aceitar os termos para continuar.');
      ok = false;
    }
    if (!ok) return;

    setEnviando(true);
    try {
      const dados = await authApi.primeiroAcesso(token, senha, aceita);
      registrarSessao({
        accessToken: dados.accessToken,
        refreshToken: dados.refreshToken,
        usuario: authApi.usuarioDeResposta(dados),
      });
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && !err.ehRede && err.codigo === 'nao_autenticado') {
        setLinkExpirado(true);
      } else {
        setErroGeral('Não foi possível criar sua senha agora. Tente de novo.');
      }
      setEnviando(false);
    }
  };

  return (
    <TelaAuthPortal>
      <form onSubmit={(e) => void aoEnviar(e)} className="flex flex-col gap-5" noValidate>
        <div className="flex flex-col gap-2">
          <h1 className="tipo-display text-tinta-forte">Bem-vindo!</h1>
          <p className="tipo-corpo text-tinta">
            Crie uma senha para acessar seu código de indicação e seus brindes.
          </p>
        </div>
        {erroGeral && <BlocoAviso>{erroGeral}</BlocoAviso>}
        <CamposSenha
          senha={senha}
          setSenha={setSenha}
          confirma={confirma}
          setConfirma={setConfirma}
          erro={erroSenha ?? undefined}
        />
        <CheckboxTermos marcado={aceita} aoMudar={setAceita} erro={erroTermos ?? undefined}>
          Li e aceito os{' '}
          <Link to="/termos" className="text-acento-texto underline">
            termos da campanha
          </Link>
          .
        </CheckboxTermos>
        <Botao type="submit" variante="primario" carregando={enviando}>
          {enviando ? 'Salvando…' : 'Criar senha e entrar'}
        </Botao>
      </form>
    </TelaAuthPortal>
  );
}
